(() => {
  const VERSION = 'epg-colour-1';
  const memory = new Map();

  function toLab(rgb) {
    const [r, g, b] = rgb.map((v) => {
      const n = v / 255;
      return n <= 0.04045 ? n / 12.92 : ((n + 0.055) / 1.055) ** 2.4;
    });
    const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
    const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
    const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
    return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
      1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
      0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
  }

  const distance = (a, b) => a.reduce((sum, value, i) => sum + (value - b[i]) ** 2, 0);

  function analysePixels(pixels) {
    const bins = new Map();
    for (let i = 0; i < pixels.length; i += 4) {
      const weight = pixels[i + 3] / 255;
      if (weight < 0.05) continue;
      const key = ((pixels[i] >> 4) << 8) | ((pixels[i + 1] >> 4) << 4) | (pixels[i + 2] >> 4);
      const bin = bins.get(key) || { key, weight: 0, rgb: [0, 0, 0] };
      bin.weight += weight;
      for (let c = 0; c < 3; c++) bin.rgb[c] += pixels[i + c] * weight;
      bins.set(key, bin);
    }
    if (!bins.size) return null;
    const points = [...bins.values()].sort((a, b) => b.weight - a.weight || a.key - b.key)
      .map((bin) => ({ ...bin, rgb: bin.rgb.map((v) => v / bin.weight) }));
    points.forEach((point) => { point.lab = toLab(point.rgb); });
    let centres = [points[0].lab];
    while (centres.length < Math.min(4, points.length)) {
      const next = points.map((p) => ({ p, score: Math.min(...centres.map((c) => distance(p.lab, c))) * Math.sqrt(p.weight) }))
        .sort((a, b) => b.score - a.score)[0];
      if (next.score < 0.0001) break;
      centres.push(next.p.lab);
    }
    let groups;
    for (let iteration = 0; iteration < 8; iteration++) {
      groups = centres.map(() => ({ weight: 0, lab: [0, 0, 0], rgb: [0, 0, 0] }));
      for (const p of points) {
        let closest = 0;
        centres.forEach((centre, i) => { if (distance(p.lab, centre) < distance(p.lab, centres[closest])) closest = i; });
        const group = groups[closest];
        group.weight += p.weight;
        for (let c = 0; c < 3; c++) {
          group.lab[c] += p.lab[c] * p.weight;
          group.rgb[c] += p.rgb[c] * p.weight;
        }
      }
      centres = groups.map((g, i) => g.weight ? g.lab.map((v) => v / g.weight) : centres[i]);
    }
    const dominant = groups.sort((a, b) => b.weight - a.weight)[0];
    const lab = dominant.lab.map((v) => v / dominant.weight);
    const rgb = dominant.rgb.map((v) => Math.round(v / dominant.weight));
    const chroma = Math.hypot(lab[1], lab[2]);
    const hue = (Math.atan2(lab[2], lab[1]) * 180 / Math.PI + 360) % 360;
    const group = lab[0] < 0.22 ? 9 : chroma < 0.035 ? (lab[0] >= 0.72 ? 0 : 8) : 1 + Math.floor(((hue + 15) % 360) / 60);
    return { lightness: lab[0], chroma, hue, group, hex: '#' + rgb.map((v) => v.toString(16).padStart(2, '0')).join('') };
  }

  function compare(a, b) {
    if (!a || !b) return a ? -1 : b ? 1 : 0;
    const hueA = Math.floor(((a.hue + 15) % 360) / 18);
    const hueB = Math.floor(((b.hue + 15) % 360) / 18);
    return a.group - b.group || (a.group > 0 && a.group < 8 ? hueA - hueB : 0) || b.lightness - a.lightness || a.chroma - b.chroma;
  }

  function orderedPage(items, page, pageSize) {
    const ordered = items.map((item, index) => ({ ...item, originalIndex: index }))
      .sort((a, b) => Number(a.available === false) - Number(b.available === false) || compare(a.palette, b.palette) || a.originalIndex - b.originalIndex);
    return { ordered, page: ordered.slice((page - 1) * pageSize, page * pageSize) };
  }

  async function mapLimit(items, limit, work, signal) {
    const result = new Array(items.length);
    let cursor = 0;
    await Promise.all(Array.from({ length: Math.min(limit, items.length) }, async () => {
      while (cursor < items.length) {
        if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
        const index = cursor++;
        result[index] = await work(items[index], index);
      }
    }));
    return result;
  }

  function imagePalette(url, signal) {
    if (!url) return Promise.resolve(null);
    const key = VERSION + ':' + url;
    if (memory.has(key)) return Promise.resolve(memory.get(key));
    try {
      const saved = JSON.parse(sessionStorage.getItem(key));
      if (saved && Number.isFinite(saved.lightness) && Number.isFinite(saved.group)) {
        memory.set(key, saved);
        return Promise.resolve(saved);
      }
    } catch (_) {}
    return new Promise((resolve, reject) => {
      const img = new Image();
      let settled = false;
      const finish = (error, palette) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        signal?.removeEventListener('abort', abort);
        img.onload = null;
        img.onerror = null;
        if (error) { img.src = ''; reject(error); return; }
        memory.set(key, palette);
        try { if (palette) sessionStorage.setItem(key, JSON.stringify(palette)); } catch (_) {}
        resolve(palette);
      };
      const abort = () => finish(new DOMException('Aborted', 'AbortError'));
      const timer = setTimeout(() => finish(new Error('Image colour analysis timed out')), 10000);
      signal?.addEventListener('abort', abort, { once: true });
      if (signal?.aborted) { abort(); return; }
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 32;
          const context = canvas.getContext('2d', { willReadFrequently: true });
          if (!context || !img.naturalWidth) throw new Error('Image unavailable');
          context.drawImage(img, 0, 0, 32, 32);
          finish(null, analysePixels(context.getImageData(0, 0, 32, 32).data));
        } catch (error) { finish(error); }
      };
      img.onerror = () => finish(new Error('Image colour data unavailable'));
      img.src = url;
    });
  }

  const api = { analysePixels, compare, orderedPage, mapLimit, imagePalette, toLab };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else window.PolewardImageColour = api;
})();
